/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { getRestEndpoint } from 'src/app/helpers/api-url';

import {
  AccessLevelsGraphQL,
  AccessLevelsGraphQLOptions,
  IIsAuthorized,
  IUser,
  IUserCreateREST,
  IUserPasswordParams,
  IUserPasswordUpdateParams,
  IUserResetPasswordParams,
  IUsersPage,
  IUsersPageParams,
  IUserUpdateREST,
  IUserUrlValidationResponse,
  IUserValidateParams,
} from '../../interfaces/user/user.interface';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(private http: HttpClient) {}

  /** @deprecated Use the version from GQL service instead */
  getUsers(usersPage?: IUsersPageParams): Observable<IUsersPage> {
    const body = {
      params: new HttpParams()
        .append('page', usersPage ? usersPage.page.toString() : '1')
        .append('pageSize', usersPage ? usersPage.pageSize.toString() : '50'),
    };

    if (usersPage?.X_GROUP_ID) {
      body.params = body.params.append('X_GROUP_ID', usersPage.X_GROUP_ID);
    }
    return this.http.get<IUsersPage>(`${getRestEndpoint()}/users`, body);
  }

  getIsUserActive(): Observable<boolean> {
    return this.http
      .get<IIsAuthorized>(`${getRestEndpoint()}/users/loggedin/isauthorized`)
      .pipe(
        map((x) => {
          return x.authorized;
        }),
      );
  }

  getLoggedinUserIdName(token?: any): Observable<IUser> {
    const headers = token
      ? {
          Authorization: `${token?.token_type} ${token.access_token}`,
        }
      : undefined;
    return this.http.get<IUser>(`${getRestEndpoint()}/users/loggedin/idname`, {
      headers,
    });
  }

  getUserAllowedGroups(): Observable<any> {
    return this.http.get<any>(`${getRestEndpoint()}/users/allowedGroups`);
  }

  updateIsUserActive(id: number, active: boolean): Observable<any> {
    return this.http.put(
      `${getRestEndpoint()}/users/${id}/active/${active}`,
      null,
    );
  }

  createUser(user: IUserCreateREST): Observable<any> {
    return this.http.post(`${getRestEndpoint()}/users`, user);
  }

  updateUser(user: IUserUpdateREST): Observable<any> {
    return this.http.put(`${getRestEndpoint()}/users`, user);
  }

  createPassword(userPasswordParams: IUserPasswordParams): Observable<any> {
    return this.http.post(
      `${getRestEndpoint()}/users/password`,
      userPasswordParams,
    );
  }

  updatePassword(userPassword: IUserPasswordUpdateParams): Observable<any> {
    return this.http.put(`${getRestEndpoint()}/users/password`, userPassword);
  }

  resetPassword(userPassword: IUserResetPasswordParams): Observable<any> {
    return this.http.post(
      `${getRestEndpoint()}/users/password/reset`,
      userPassword,
    );
  }

  updatePasswordAfterReset(userPassword: IUserPasswordParams): Observable<any> {
    return this.http.put(
      `${getRestEndpoint()}/users/password/reset`,
      userPassword,
    );
  }

  validateUrl(
    validateUrlParams: IUserValidateParams,
  ): Observable<IUserUrlValidationResponse> {
    const body = {
      params: new HttpParams().append(
        'userLinkUrl',
        validateUrlParams.userLinkUrl.toString(),
      ),
    };
    return this.http.get<IUserUrlValidationResponse>(
      `${getRestEndpoint()}/users/links/validate`,
      body,
    );
  }

  validateUrlAndPin(
    validateUrlParams: IUserValidateParams,
  ): Observable<IUserUrlValidationResponse> {
    const body = {
      params: new HttpParams().append(
        'userLinkUrl',
        validateUrlParams.userLinkUrl.toString(),
      ),
    };

    if (validateUrlParams.pin) {
      body.params = body.params.append('pin', validateUrlParams.pin.toString());
    }

    return this.http.get<IUserUrlValidationResponse>(
      `${getRestEndpoint()}/users/links/validate/pin`,
      body,
    );
  }

  isAvilableByAccessLevel(
    userAccessLevel: AccessLevelsGraphQL,
    referenceAccessLevel?: AccessLevelsGraphQL,
  ) {
    const accessLevels = Object.values(AccessLevelsGraphQLOptions);
    return (
      accessLevels.indexOf(userAccessLevel) >=
      accessLevels.indexOf(referenceAccessLevel ?? 'SECURE_CONNECT')
    );
  }
}
